import { faArrowAltCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowAltCircleLeft'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import startCase from 'lodash/startCase'
import React, { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { GET_ONE, useClient } from '@peracto/client'
import { useConfig } from '@peracto/peracto-config'
import { Group, Heading } from '@peracto/peracto-ui'

import {
    DEFAULT_ERROR_MESSAGE,
    POLL_INTERVAL_MS,
    formatImportTypeLabel,
    importLogId,
    isCreatedWithinLast24Hours,
} from '../shared'

const DetailRow = ({ label, children }) => (
    <div className="row py-2">
        <div className="col-sm-4 text-muted">{label}</div>
        <div className="col-sm-8">{children}</div>
    </div>
)

const UserImportDetail = () => {
    const { id } = useParams()
    const { client } = useClient()
    const config = useConfig()
    const formatDate = config.get('user_format_date', config.get('format_date'))
    const [record, setRecord] = useState(null)
    const [loading, setLoading] = useState(true)
    const [fetchError, setFetchError] = useState(null)

    const resolvedId = importLogId(record) ?? id

    const load = useCallback(async () => {
        if (!id) return
        try {
            setFetchError(null)
            const { data } = await client(GET_ONE, 'import-logs', {
                id: `/import-logs/${id}`,
            })
            setRecord(data)
        } catch (e) {
            console.error(e)
            setFetchError(
                e?.error?.body?.['hydra:description'] ?? DEFAULT_ERROR_MESSAGE
            )
            setRecord(null)
        } finally {
            setLoading(false)
        }
    }, [client, id])

    useEffect(() => {
        setLoading(true)
        load()
    }, [load])

    const shouldPoll =
        record?.status === 'processing' && isCreatedWithinLast24Hours(record?.createdAt)

    useEffect(() => {
        if (!shouldPoll) return undefined
        const t = setInterval(() => {
            load()
        }, POLL_INTERVAL_MS)
        return () => clearInterval(t)
    }, [shouldPoll, load])

    const downloadUploadedFile = useCallback(async () => {
        const downloadId = importLogId(record) ?? id
        if (!downloadId) return
        try {
            const { data: downloadData } = await client(GET_ONE, 'import-logs', {
                id: `/import-logs/${downloadId}/pre-signed-url`,
            })

            if (downloadData?.downloadUrl) {
                window.location.href = downloadData.downloadUrl
            } else {
                toast.error(DEFAULT_ERROR_MESSAGE)
            }
        } catch (e) {
            console.error(e)
            const status = e?.status ?? e?.error?.status ?? e?.response?.status
            const hydraDescription = e?.error?.body?.hasOwnProperty('hydra:description')
                ? e.error.body['hydra:description']
                : null

            if (status === 400) {
                toast.error(
                    hydraDescription ?? 'File is not ready to download yet. Please try again.'
                )
            } else {
                toast.error(hydraDescription ?? DEFAULT_ERROR_MESSAGE)
            }
        }
    }, [client, record, id])

    const renderStatus = (status) => (
        <span
            className={classNames('badge', 'badge-pill', {
                'badge-info': status === 'processing',
                'badge-success': status === 'complete',
                'badge-danger': status === 'error',
            })}
        >
            {startCase(status || '')}
        </span>
    )

    const formatDt = (value) => {
        if (!value) return '—'
        try {
            return formatDate(new Date(value))
        } catch {
            return value
        }
    }

    const titleId = String(resolvedId ?? id ?? '')

    if (loading && !record) {
        return (
            <>
                <Helmet>
                    <title>{titleId ? `Import #${titleId} | Peracto` : 'Import | Peracto'}</title>
                </Helmet>
                <div className="container-fluid">
                    <p className="text-muted py-4">Loading import…</p>
                </div>
            </>
        )
    }

    if (fetchError && !record) {
        return (
            <>
                <Helmet>
                    <title>{'Import | Peracto'}</title>
                </Helmet>
                <div className="container-fluid">
                    <div className="alert alert-danger mt-3" role="alert">
                        {fetchError}
                    </div>
                    <Link className="btn btn-outline-primary" to="/manage-user-data">
                        <FontAwesomeIcon icon={faArrowAltCircleLeft} className="mr-2" />
                        Back
                    </Link>
                </div>
            </>
        )
    }

    return (
        <>
            <Helmet>
                <title>{`Import #${titleId} | Peracto`}</title>
            </Helmet>
            <Heading name={`Import #${resolvedId ?? id}`}>
                <div className="d-flex align-items-center ml-2 flex-wrap">
                    <Link
                        className="btn btn-outline-primary mr-2 mb-2 mb-sm-0"
                        to="/manage-user-data"
                    >
                        <FontAwesomeIcon icon={faArrowAltCircleLeft} className="mr-2" />
                        Back
                    </Link>
                </div>
            </Heading>

            <div className="row">
                <div className="col">
                    <Group id="import-log-detail" name="Import details">
                        {shouldPoll && (
                            <p className="text-muted small mb-3">
                                This import is still processing. This page refreshes every{' '}
                                {POLL_INTERVAL_MS / 1000} seconds.
                            </p>
                        )}

                        <DetailRow label="ID">{resolvedId ?? '—'}</DetailRow>
                        <DetailRow label="Type">{formatImportTypeLabel(record?.type)}</DetailRow>
                        <DetailRow label="Status">{renderStatus(record?.status)}</DetailRow>
                        <DetailRow label="Original filename">
                            {record?.originalFilename ? (
                                <button
                                    type="button"
                                    className="btn btn-link text-primary p-0 text-left"
                                    onClick={() => downloadUploadedFile()}
                                    data-testid="import-detail-filename-download"
                                >
                                    {record.originalFilename}
                                </button>
                            ) : (
                                '—'
                            )}
                        </DetailRow>
                        <DetailRow label="Row Count">
                            {record?.rowCount != null ? record.rowCount : '—'}
                        </DetailRow>
                        <DetailRow label="Errors">
                            {record?.errorRowCount != null ? record.errorRowCount : '—'}
                        </DetailRow>
                        <DetailRow label="Created">{formatDt(record?.createdAt)}</DetailRow>
                        <DetailRow label="Last updated">{formatDt(record?.modifiedAt)}</DetailRow>
                        <DetailRow label="Started by">
                            {record?.user ? record.user : '—'}
                        </DetailRow>
                    </Group>
                </div>
            </div>
        </>
    )
}

export default UserImportDetail
